import { useTranslation } from "react-i18next";
import { Typography, Chip, Box, Stack, Avatar } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { MissionEnumsLists, TaskEnums, TaskEnumsLists } from "../../../../common/constants";
import { GetEstimatedUrgencyColor, GetSecondaryUrgencyDescription } from "../../../../common/mission";
import { NavLink } from "react-router-dom"
import { formatText, truncate } from "../../../../common/helpers";
import { Close as CloseIcon } from "@mui/icons-material";
import { useMemo } from "react";

const useStyles = makeStyles()(
  (theme) => ({
    link: {
      textDecoration: "none",
      color: "inherit",
      width: "100%"
    },
    card: {
      background: theme.palette.background.default,
      padding: theme.spacing(2),
      borderRadius: theme.spacing(2),
      boxShadow: `0 0 1px 0 rgba(0, 0, 0, 0.04), 0 4px 4px 0 rgba(0, 0, 0, 0.02)`
    },
    root: {
      width: "100%",
      position: "relative",
      cursor: "pointer",
      "&:after": {
        zIndex: 3,
        position: "absolute",
        height: "100%",
        width: "100%",
        top: "0px",
        left: "0px",
        opacity: 0,
        background: theme.palette.action.hover,
        borderRadius: theme.spacing(2),
        content: "''",
        display: "block",
        pointerEvents: "none"
      },
      "&:hover:after": {
        opacity: 1
      }
    },
    highlight: {
      "&:after": {
        opacity: 1,
        border: `1px solid ${theme.palette.divider}`
      }
    },
    pill: {
      width: "6px",
      borderRadius: "3px",
      minHeight: "100%"
    },
    fullwidth: {
      width: "100%",
      position: "relative"
    },
    noDriverAvatar: {
      border: `2px dashed ${theme.palette.divider}`,
      background: "none"
    },
    driverRejectedAvatar: {
      border: `2px solid ${theme.palette.error.main}`,
      background: theme.palette.error.background
    },
    driverPendingAvatar: {
      border: `2px solid ${theme.palette.divider}`,
      background: "none",
      "& img": {
        width: "12px",
        height: "12px"
      }
    },
    marginTop12: {
      marginTop: theme.spacing(1)
    },
    marginTop4: {
      marginTop: theme.spacing(0.5)
    },
    fontWeight400: {
      fontWeight: 400
    },
    tag: {
      color: theme.palette.text.secondary,
      fontSize: "10px"
    }
  })
);

export default function TaskItem(props) {
  const { task, highlight, onMouseEnter, onMouseLeave, onClick, card } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();

  const targetType = task.missionTarget.type;
  const targetDetails = targetType === 1 ? task.missionTarget.carDetails : (targetType === 2 ? task.missionTarget.boatDetails : null);

  const { execution, driverData, resolvedAtLocation, route } = task;
  const { driver, driverAssignedState } = execution;

  const waypoints = route?.waypoints || [];
  const location = waypoints[0];
  const destination = waypoints[waypoints.length - 1];

  const formattedLocation = truncate(formatText(", ", location?.place, location?.title || location?.address), 36);
  const formattedDestination = waypoints?.length > 1 && !resolvedAtLocation && !!destination ? truncate(formatText(", ", destination?.place, destination?.title || destination?.address), 36) : null;

  const formattedDetails = truncate(task.missionDetails?.description, 64);
  const formattedRegistrationAndCause = formatText(" • ", targetDetails?.registration, task?.missionDetails?.cause?.name);
  const formattedTarget = formatText(" ", targetDetails?.make, targetDetails?.model) || t(`mission:enums:targetType:${MissionEnumsLists.TargetType[targetType]}`);

  const tags = task.missionDetails?.tags || [];


  const { color, title, fromNow, description } = useMemo(() => {

    return {
      title: t(`task:enums:urgencyReason:${TaskEnumsLists.UrgencyReason[task.urgencyReason]}`),
      color: GetEstimatedUrgencyColor(task.urgency),
      fromNow: !!task.urgencyTime ? `(${task.urgencyTime?.fromNow(true)})` : null,
      description: GetSecondaryUrgencyDescription(t, task),
    }
  }, [task, t])

  return (
    <NavLink to={`./${task.id}`} className={classes.link}>
      <Box
        className={`${classes.root} ${highlight ? classes.highlight : ""} ${card ? classes.card : ""}`}
        onMouseEnter={typeof onMouseEnter === "function" ? ev => onMouseEnter(ev, task) : undefined}
        onMouseLeave={typeof onMouseLeave === "function" ? ev => onMouseLeave(ev, task) : undefined}
        onClick={typeof onClick === "function" ? ev => onClick(ev, task) : undefined}
      >
        <Stack direction="row" className={classes.fullwidth} spacing={2}>

          <Box className={classes.pill} sx={{ backgroundColor: color }}></Box>

          <Stack direction="column" className={classes.fullwidth} justifyContent="flex-start" alignItems="flex-start">

            <Stack direction="row" alignItems="baseline" justifyContent="space-between" className={classes.fullwidth}>
              <Typography variant="subtitle1">
                {formattedTarget}
              </Typography>

              <Typography variant="body2" color="textDisabled" fontWeight={500}>
                #{task.missionNumber}.{task.number}
              </Typography>
            </Stack>

            <Typography variant="body2" fontWeight={500} color={task.urgency === TaskEnums.EstimatedUrgency.Critical ? "error" : "textPrimary"}>
              {title} {fromNow} • {description}
            </Typography>

            <Typography variant="caption" color="textSecondary" fontWeight={500} className={classes.marginTop4}>
              {formattedLocation}
              {!!formattedDestination ? <span className={classes.fontWeight400}><br />↳ {formattedDestination}</span> : ""}
            </Typography>

            <Typography variant="caption" color="textSecondary">
              {formattedRegistrationAndCause}
              {!!formattedRegistrationAndCause && !!formattedDetails ? <br /> : null}
              {formattedDetails}
            </Typography>


            <Stack direction="row" alignItems="center" justifyContent="space-between" className={`${classes.fullwidth} ${classes.marginTop12}`}>

              <Stack direction="row" spacing={0.5} alignItems="center">
                {
                  driverAssignedState === TaskEnums.DriverAssignedState.Rejected ? (
                    <Avatar
                      className={classes.driverRejectedAvatar}
                      sx={{ width: 20, height: 20 }}
                    >
                      <CloseIcon color="error" />
                    </Avatar>
                  ) : driverAssignedState === TaskEnums.DriverAssignedState.Pending ? (
                    <Avatar
                      sx={{ width: 20, height: 20 }}
                      className={classes.driverPendingAvatar}
                      src="/assets/icons/animations/hourglass-24.apng"
                    />
                  ) : (
                    <Avatar
                      sx={{ width: 20, height: 20 }}
                      src={driverData?.picture}
                      alt={driver?.name}
                    />
                  )
                }

                <Typography variant="caption" fontWeight={500} color={driverAssignedState === TaskEnums.DriverAssignedState.Rejected ? "error" : driverAssignedState === TaskEnums.DriverAssignedState.Accepted ? "textPrimary" : "textDisabled"}>
                  {driverData?.name || t("dispatch:table:noDriverAssigned")}
                </Typography>
              </Stack>


              <Stack direction="row" spacing={1}>
                {tags.length >= 1 ? <Chip className={classes.tag} label={tags[0].name} size="small" /> : null}
                {tags.length > 1 ? <Chip className={classes.tag} label={`+${tags.length - 1}`} size="small" /> : null}
              </Stack>

            </Stack>
          </Stack>
        </Stack>
      </Box>
    </NavLink>
  );
}

